import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MapPin, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitch from "@/components/LanguageSwitch";
import ThemeToggle from "@/components/ThemeToggle";
import CinematicToggle from "@/components/CinematicToggle";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { HERO_DIM_OPACITY } from "@/constants/theme";

const DARK_LOGO = "/dark_nav_logo.png";
const LIGHT_LOGO = "/light_nav_logo.png";

// Logo display size (px). Tweak these to enlarge/shrink the navbar logo.
// Height is the main control; width is auto unless NAVBAR_LOGO_WIDTH is set.
const NAVBAR_LOGO_HEIGHT = 105;
const NAVBAR_LOGO_WIDTH = null; // e.g. 120, or null for auto (keeps aspect ratio)

const PHONE_NAV_QUERY = "(max-width: 767px), ((orientation: landscape) and (max-height: 540px) and (max-width: 1023px))";

function usePhoneNav() {
  const [isPhoneNav, setIsPhoneNav] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia(PHONE_NAV_QUERY).matches : false
  ));

  useEffect(() => {
    const media = window.matchMedia(PHONE_NAV_QUERY);
    const sync = () => setIsPhoneNav(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return isPhoneNav;
}

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFinancingDropdownOpen, setIsFinancingDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const isPhoneNav = usePhoneNav();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark, cinematicAvailable, heroVideoActive } = useTheme();

  // In light mode, white text at top ONLY on landing page (over hero image)
  // On all other pages, always black text in light mode
  const isLandingPage = location.pathname === '/';
  const isNavActive = (href) => {
    if (href === "/inventory") {
      return location.pathname === "/inventory" || location.pathname.startsWith("/inventory/");
    }
    return location.pathname === href;
  };
  const useWhiteText = isDark || (isLandingPage && !isScrolled);

  const scrollToContact = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname === "/" && location.hash === "#contact") {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    navigate("/#contact");
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsFinancingDropdownOpen(false);
    setIsServicesDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    if (!isPhoneNav) setIsMobileMenuOpen(false);
  }, [isPhoneNav]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const { body, documentElement } = document;
    const scrollY = window.scrollY;
    const prev = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: documentElement.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };
    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    return () => {
      body.style.overflow = prev.bodyOverflow;
      documentElement.style.overflow = prev.htmlOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isMobileMenuOpen]);

  // Text color helpers
  const activeTextCls = useWhiteText ? 'text-white' : 'text-gray-900';
  const inactiveTextCls = useWhiteText
    ? 'text-gray-300 hover:text-white'
    : 'text-gray-600 hover:text-gray-900';
  const brandTextCls = useWhiteText ? 'text-white' : 'text-gray-900';
  const subtitleTextCls = useWhiteText ? 'text-gray-400' : 'text-gray-500';

  // Logo: dark mode = blue logo, light mode = maroon logo
  const logoSrc = isDark ? DARK_LOGO : LIGHT_LOGO;

  const navLinks = [
    { key: "home", name: t('nav.home'), href: "/" },
    { key: "inventory", name: t('nav.inventory'), href: "/inventory" },
    {
      key: "financing",
      name: t('nav.financing'),
      isDropdown: true,
      dropdownItems: [
        { name: t('nav.applyFinancing'), href: "/financing" },
        { name: t('nav.loanCalculator'), href: "/loan-calculator" }
      ]
    },
    {
      key: "services",
      name: t('nav.services'),
      isDropdown: true,
      dropdownItems: [
        { name: t('nav.tradeIn'), href: "/trade-in" },
        { name: t('nav.scheduleService'), href: "/schedule-service" }
      ]
    },
    { key: "contact", name: t('nav.contact'), href: "#contact", isScrollLink: true },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0, opacity: heroVideoActive ? HERO_DIM_OPACITY : 1 }}
        transition={{ duration: 0.5 }}
        style={{ pointerEvents: heroVideoActive ? 'none' : 'auto' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? isDark
              ? "bg-[#050505]/95 backdrop-blur-md border-b border-white/10"
              : "bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        {/* Full-width bar: brand pinned to left viewport edge on every screen size */}
        <div className={`w-full flex items-center justify-between h-20 ${
          isPhoneNav ? "relative pl-0 pr-10 landscape:pr-32 gap-1" : "pl-2 sm:pl-3 md:pl-4 pr-3 sm:pr-4 md:pr-6 lg:pr-8"
        }`}>
            {/* Logo + brand — always top-left. On phones, theme + apply + inventory sit next to the logo. */}
            <div className={`flex items-center min-w-0 ${isPhoneNav ? "gap-0 -ml-2" : ""}`}>
            <Link
              to="/"
              className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0"
              data-testid="nav-logo"
            >
              <img
                src={logoSrc}
                alt="Drive Now Autohaus Logo"
                className="object-contain shrink-0"
                style={{
                  height: NAVBAR_LOGO_HEIGHT,
                  width: NAVBAR_LOGO_WIDTH ?? "auto",
                  maxHeight: "100%",
                }}
              />
              <div className={`min-w-0 ${isPhoneNav ? "hidden" : "hidden md:block"}`}>
                <span className={`block whitespace-nowrap font-unbounded font-bold text-sm sm:text-base md:text-lg tracking-tight transition-colors duration-300 ${brandTextCls}`}>
                  DRIVE NOW
                </span>
                <span className={`block text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] -mt-0.5 sm:-mt-1 transition-colors duration-300 ${subtitleTextCls}`}>
                  Autohaus
                </span>
              </div>
            </Link>
            {isPhoneNav && (
              <div className="flex items-center gap-1 -ml-2 min-w-0">
                <ThemeToggle useWhiteText={useWhiteText} compact />
                <Link
                  to="/financing"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`inline-flex items-center justify-center px-1.5 py-1 max-w-[6.25rem] text-[9px] leading-tight font-semibold uppercase tracking-wide text-center border whitespace-normal break-words ${
                    isDark || useWhiteText
                      ? "border-white/40 text-white hover:bg-white/10"
                      : "border-gray-900/25 text-gray-900 hover:bg-black/5"
                  }`}
                  data-testid="mobile-nav-apply-financing"
                >
                  {t('nav.applyFinancing')}
                </Link>
                <Link
                  to="/inventory"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-1.5 py-1 max-w-[4.75rem] text-[9px] leading-tight font-semibold uppercase tracking-wide text-center shrink-0 ${
                    isDark || useWhiteText
                      ? "bg-white text-black hover:bg-gray-200"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                  data-testid="mobile-nav-view-inventory"
                >
                  {t('nav.viewInventory')}
                </Link>
              </div>
            )}
            </div>

            {/* Desktop Navigation — stays right of the brand */}
            <div className={`${isPhoneNav ? "hidden" : "hidden md:flex"} items-center gap-6 lg:gap-8 flex-1 justify-center min-w-0 px-4`}>
              {navLinks.map((link) => (
                link.isDropdown ? (
                  <div
                    key={link.key}
                    className="relative"
                    onMouseEnter={() => {
                      if (link.key === "financing") setIsFinancingDropdownOpen(true);
                      if (link.key === "services") setIsServicesDropdownOpen(true);
                    }}
                    onMouseLeave={() => {
                      if (link.key === "financing") setIsFinancingDropdownOpen(false);
                      if (link.key === "services") setIsServicesDropdownOpen(false);
                    }}
                  >
                    <button
                      className={`text-sm font-medium uppercase tracking-wider transition-colors duration-300 flex items-center gap-1 ${
                        link.dropdownItems.some(item => location.pathname === item.href)
                          ? activeTextCls
                          : inactiveTextCls
                      }`}
                      data-testid={`nav-link-${link.key}`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${
                        (link.key === "financing" && isFinancingDropdownOpen) ||
                        (link.key === "services" && isServicesDropdownOpen)
                          ? 'rotate-180'
                          : ''
                      }`} />
                    </button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {((link.key === "financing" && isFinancingDropdownOpen) ||
                        (link.key === "services" && isServicesDropdownOpen)) && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className={`absolute top-full left-0 mt-2 w-56 shadow-xl z-50 ${
                            isDark
                              ? "bg-[#0a0a0a] border border-white/10"
                              : "bg-white border border-gray-200"
                          }`}
                        >
                          {link.dropdownItems.map((item, index) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-3 text-sm font-['Manrope'] transition-colors ${
                                location.pathname === item.href
                                  ? isDark
                                    ? "bg-blue-500/10 text-white"
                                    : "bg-red-50 text-red-600"
                                  : isDark
                                    ? "text-gray-400 hover:bg-white/5 hover:text-white"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                              } ${index !== link.dropdownItems.length - 1 ? isDark ? 'border-b border-white/10' : 'border-b border-gray-100' : ''}`}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : link.isScrollLink ? (
                  <button
                    key={link.key}
                    onClick={scrollToContact}
                    className={`text-sm font-medium uppercase tracking-wider transition-colors duration-300 ${inactiveTextCls}`}
                    data-testid={`nav-link-${link.key}`}
                  >
                    {link.name}
                  </button>
                ) : (
                  <Link
                    key={link.key}
                    to={link.href}
                    className={`text-sm font-medium uppercase tracking-wider transition-colors duration-300 ${
                      isNavActive(link.href) ? activeTextCls : inactiveTextCls
                    }`}
                    data-testid={`nav-link-${link.key}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* Contact Info & CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <ThemeToggle useWhiteText={useWhiteText} />
              {cinematicAvailable && <CinematicToggle useWhiteText={useWhiteText} />}
              <LanguageSwitch useWhiteText={useWhiteText} />

              <a
                href="tel:+17082226100"
                className={`flex items-center gap-1.5 shrink-0 transition-colors duration-300 ${inactiveTextCls}`}
              >
                <Phone className="h-4 w-4 shrink-0" />
                <span className="text-xs whitespace-nowrap">(708) 222-6100</span>
              </a>
              <Link to="/inventory">
                <Button
                  className={`rounded-none px-6 py-2 font-semibold text-sm uppercase tracking-wider ${
                    isDark
                      ? "bg-white text-black hover:bg-gray-200"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                  data-testid="nav-view-inventory-btn"
                >
                  {t('nav.viewInventory')}
                </Button>
              </Link>
            </div>

            {/* Mobile header: hamburger stays pinned on the right so it is never cropped */}
            <div className={`${isPhoneNav ? "absolute right-0.5 top-1/2 -translate-y-1/2 flex" : "hidden"} items-center gap-0.5 shrink-0`}>
              <div className="hidden landscape:block shrink-0">
                <LanguageSwitch useWhiteText={useWhiteText} compact />
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-1.5 shrink-0 transition-colors duration-300 ${useWhiteText ? 'text-white' : 'text-gray-900'}`}
                data-testid="mobile-menu-toggle"
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isPhoneNav && isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className={`fixed left-0 right-0 bottom-0 top-20 z-40 flex flex-col ${isDark ? 'bg-[#050505]' : 'bg-white'}`}
            data-testid="mobile-menu"
          >
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-6 [-webkit-overflow-scrolling:touch]">
              <div className="landscape:hidden">
                <LanguageSwitch useWhiteText={isDark} />
              </div>
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.isDropdown ? (
                    <div>
                      <div className={`text-2xl font-unbounded font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {link.name}
                      </div>
                      <div className="pl-4 space-y-3">
                        {link.dropdownItems.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className={`block text-lg font-['Manrope'] transition-colors ${
                              isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                            }`}
                            data-testid={`mobile-nav-link-${item.href.replace('/', '')}`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : link.isScrollLink ? (
                    <button
                      onClick={scrollToContact}
                      className={`block text-2xl font-unbounded font-semibold text-left ${isDark ? 'text-white' : 'text-gray-900'}`}
                      data-testid={`mobile-nav-link-${link.key}`}
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className={`block text-2xl font-unbounded font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
                      data-testid={`mobile-nav-link-${link.key}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={`pt-6 border-t space-y-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}
              >
                <a
                  href="tel:+17082226100"
                  className={`flex items-center gap-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  <Phone className="h-5 w-5" />
                  <span>(708) 222-6100</span>
                </a>
                <a
                  href="https://maps.google.com/?q=5401+W+Cermak+Rd,+Cicero,+IL+60804"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  <MapPin className="h-5 w-5" />
                  <span>5401 W Cermak Rd, Cicero, IL</span>
                </a>
                <Link to="/inventory" className="block pt-4">
                  <Button
                    className={`w-full rounded-none py-4 font-semibold uppercase tracking-wider ${
                      isDark
                        ? 'bg-white text-black hover:bg-gray-200'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                    data-testid="mobile-view-inventory-btn"
                  >
                    {t('nav.viewInventory')}
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
